import React, { FormEvent, useCallback } from 'react';
import { IdolFormProps } from './types';
import useIdolForm from './hooks/useIdolForm';
import useUniqueIdols from './hooks/useUniqueIdols';
import Modal from 'components/UI/Modal';
import ManualCreationForm from './ManualCreationForm';
import PasteImportForm from './PasteImportForm';
import { UNIQUE_IDOLS } from './constants/uniqueIdols';

const IdolFormContainer: React.FC<IdolFormProps> = ({ isOpen, onClose, onAddIdol }) => {
  // Use the idol form hook
  const {
    newIdolName,
    setNewIdolName,
    newIdolSize,
    setNewIdolSize,
    prefixes,
    setPrefixes,
    suffixes,
    setSuffixes,
    availablePrefixes,
    availableSuffixes,
    // Updated properties
    prefixContentTypes,
    setSelectedPrefixContentType,
    suffixContentTypes,
    setSelectedSuffixContentType,
    showPasteInput,
    setShowPasteInput,
    pasteInput,
    setPasteInput,
    pasteError,
    handlePaste,
    handleDirectPaste,
    createRegularIdol,
    resetForm
  } = useIdolForm(onAddIdol, onClose);

  // Use the refactored unique idols hook
  const {
    selectedUniqueIdol,
    setSelectedUniqueIdol,
    idolModifiers,
    setIdolModifiers,
    selectedVariableModifiers,
    setSelectedVariableModifiers,
    updateModifierValue,
    replaceVariableModifier,
    createUniqueIdol,
    isUniqueFormValid,
    resetUniqueIdolForm,
    isVariableModifierSelected
  } = useUniqueIdols();

  // This effect ensures form is reset when modal is closed
  React.useEffect(() => {
    if (!isOpen) {
      // Reset all form state when modal is closed
      resetForm();
      resetUniqueIdolForm();
    }
  }, [isOpen, resetForm, resetUniqueIdolForm]); // IMPORTANT: Remove all function dependencies
  
  // Handle size change with proper type compatibility
  const handleUniqueIdolCheck = useCallback((value: React.SetStateAction<string>) => {
    // Call the original setState function first
    setNewIdolSize(value);
    
    // If the value is a function, call it with the current state to get the new value
    const newSize = typeof value === 'function' ? value(newIdolSize) : value;
    
    // When switching to UNIQUE size, clear the selected unique idol
    if (newSize === 'UNIQUE') {
      setSelectedUniqueIdol('');
    }
  }, [newIdolSize, setNewIdolSize, setSelectedUniqueIdol]);
  
  // Handle form submission
  const handleSubmit = useCallback((e: FormEvent) => {
    e.preventDefault();
    
    try {
      // Handle unique idols differently
      if (newIdolSize === 'UNIQUE') {
        // If we have a name but no selection, try to find the matching idol first
        if (!selectedUniqueIdol && newIdolName) {
          const matchingIdol = UNIQUE_IDOLS.find(idol => idol.name === newIdolName);
          if (matchingIdol) {
            // Create idol with the found ID
            const newIdol = createUniqueIdol(matchingIdol.name);
            onAddIdol(newIdol);
            onClose();
            return;
          }
        }

        // Normal validation path
        if (!selectedUniqueIdol) {
          throw new Error("Please select a unique idol.");
        }

        if (!isUniqueFormValid()) {
          const selectedIdolData = UNIQUE_IDOLS.find(idol => idol.id === selectedUniqueIdol);
          if (selectedIdolData?.isSpecial && selectedIdolData?.variableModifiersRequired) {
            throw new Error(`Please select exactly ${selectedIdolData.variableModifiersRequired} variable modifiers.`);
          } else {
            throw new Error("Please complete the unique idol configuration.");
          }
        }

        const newIdol = createUniqueIdol(newIdolName);
        onAddIdol(newIdol);
      } else {
        // For regular idols
        const newIdol = createRegularIdol();
        onAddIdol(newIdol);
      }
      
      onClose();
    } catch (error) {
      console.error("Form submission error:", error);
      alert(error instanceof Error ? error.message : String(error));
    }
  }, [
    newIdolSize, 
    selectedUniqueIdol, 
    newIdolName, 
    createUniqueIdol, 
    isUniqueFormValid, 
    createRegularIdol, 
    onAddIdol,
    onClose
  ]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create New Idol">
      {/* Toggle between manual creation and paste import */}
      <div className="flex mb-4 bg-gray-800 rounded">
        <button
          type="button"
          className={`flex-1 py-2 px-4 text-center ${!showPasteInput ? 'bg-blue-700 text-white' : 'bg-gray-800 text-gray-300'}`}
          onClick={() => setShowPasteInput(false)}
        >
          Manual Creation
        </button>
        <button
          type="button"
          className={`flex-1 py-2 px-4 text-center ${showPasteInput ? 'bg-blue-700 text-white' : 'bg-gray-800 text-gray-300'}`}
          onClick={() => setShowPasteInput(true)}
        >
          Import from Clipboard
        </button>
      </div>
      
      {showPasteInput ? (
        // Paste Import Form
        <PasteImportForm 
          pasteInput={pasteInput}
          setPasteInput={setPasteInput}
          pasteError={pasteError}
          handlePaste={handlePaste}
          handleDirectPaste={handleDirectPaste}
          onClose={onClose}
        />
      ) : (
        // Manual Creation Form with updated prop names
        <ManualCreationForm 
          newIdolName={newIdolName}
          setNewIdolName={setNewIdolName}
          newIdolSize={newIdolSize}
          setNewIdolSize={handleUniqueIdolCheck}
          prefixes={prefixes}
          setPrefixes={setPrefixes}
          suffixes={suffixes}
          setSuffixes={setSuffixes}
          availablePrefixes={availablePrefixes}
          availableSuffixes={availableSuffixes}
          prefixContentTypes={prefixContentTypes}
          setSelectedPrefixContentType={setSelectedPrefixContentType}
          suffixContentTypes={suffixContentTypes}
          setSelectedSuffixContentType={setSelectedSuffixContentType}
          handleSubmit={handleSubmit}
          onClose={onClose}
          // Unique idol props
          selectedUniqueIdol={selectedUniqueIdol}
          setSelectedUniqueIdol={setSelectedUniqueIdol}
          idolModifiers={idolModifiers}
          setIdolModifiers={setIdolModifiers}
          selectedVariableModifiers={selectedVariableModifiers}
          setSelectedVariableModifiers={setSelectedVariableModifiers}
          updateModifierValue={updateModifierValue}
          replaceVariableModifier={replaceVariableModifier}
          isVariableModifierSelected={isVariableModifierSelected}
          isUniqueFormValid={isUniqueFormValid}
        />
      )}
    </Modal>
  );
};

export default React.memo(IdolFormContainer);